import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { generatePath } from '@/services/router/generate-path';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { importStepHref, importStepLegacyHref, type ImportWaitingStep } from '@/features/imports';
import { useImportId } from './[importId]/use-import-id';

type ImportsListRouteViewProps = {
  /** The migrated page, mounted only once the flag is on. */
  children: ReactNode;
};

/**
 * The legacy `/journeys/imports` URL — what the embedded app and every existing link point at.
 *
 * Only the index. The upload screen and the setup wizard under `/journeys/imports` are still the
 * embedded app's, and keep falling through to the catch-all.
 */
export function JourneysImportsHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.CsvImportsMigration);
  const router = useRouter();
  const migratedHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: '/workspaces/$workspaceId/imports', params: { workspaceId } }).href,
  );

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

/**
 * The legacy `/journeys/imports/new` URL, the twin of the migrated upload screen.
 *
 * Reached from the segment and object pages, which pass the type and ids in the query string —
 * `useMigrationHandoffHref` carries those across, so a seeded link keeps its seeds.
 */
export function JourneysNewImportHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.CsvImportsMigration);
  const router = useRouter();
  const migratedHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: '/workspaces/$workspaceId/imports/new', params: { workspaceId } }).href,
  );

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

type MigratedImportsRouteProps = ImportsListRouteViewProps & {
  /** The `journeys` URL this screen hands back to while the flag is off. */
  legacyPath: (workspaceId: string) => string;
};

/**
 * A migrated `/imports` screen: this app owns it while the flag is on, and hands back to the
 * `journeys` twin the embedded app serves while it is off — the exact inverse of the handoffs
 * above. The two can't loop, because each fires in the opposite flag state.
 *
 * The permission gate is here rather than on each screen because that is where the embedded app
 * puts it: `app/pods/imports/route.ts` gates the whole `/imports` tree on `journeys/imports/view`
 * from the parent route, and every screen under it inherits that. Creating and editing are
 * separate permissions that the screens themselves answer, so a viewer who may not create still
 * reaches them and sees them read-only.
 */
function MigratedImportsRoute({ legacyPath, children }: MigratedImportsRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.CsvImportsMigration);
  const legacyHref = useMigrationHandoffHref(legacyPath);
  // Readiness gates on the resolved workspace, not the URL segment: a page's reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId/journeys', { workspaceId: workspace?.id ?? 'last' })}
      >
        {children}
      </ProtectedRoute>
    </MigrationRouteView>
  );
}

/** The migrated `/imports` list. */
export function ImportsListRouteView({ children }: ImportsListRouteViewProps) {
  const router = useRouter();

  return (
    <MigratedImportsRoute
      legacyPath={(workspaceId) =>
        router.buildLocation({ to: '/workspaces/$workspaceId/journeys/imports', params: { workspaceId } }).href
      }
    >
      {children}
    </MigratedImportsRoute>
  );
}

/** The migrated `/imports/new` upload screen. */
export function NewImportRouteView({ children }: ImportsListRouteViewProps) {
  const router = useRouter();

  return (
    <MigratedImportsRoute
      legacyPath={(workspaceId) =>
        router.buildLocation({ to: '/workspaces/$workspaceId/journeys/imports/new', params: { workspaceId } }).href
      }
    >
      {children}
    </MigratedImportsRoute>
  );
}

type WizardStepRouteViewProps = ImportsListRouteViewProps & {
  /** Which step this URL is, so the hand-off knows its twin. */
  step: ImportWaitingStep;
};

/**
 * A migrated wizard step, gated and handed back like every other `/imports` screen.
 *
 * The wizard is migrated a step at a time, so the reader crosses between the two apps part-way
 * through it while the flag is on. That is only ever an internal rollout state — the flag stays
 * off until every step has landed (FPS-2702) — and it is what lets each step be exercised as it
 * is built rather than all at the end.
 */
export function WizardStepRouteView({ step, children }: WizardStepRouteViewProps) {
  const importId = useImportId();

  return (
    <MigratedImportsRoute legacyPath={(workspaceId) => importStepLegacyHref(Number(workspaceId), importId, step)}>
      {children}
    </MigratedImportsRoute>
  );
}

type JourneysWizardStepHandoffProps = {
  /** Which step this URL is, so the forward knows its migrated address. */
  step: ImportWaitingStep;
};

/**
 * The legacy `/journeys/imports/:importId/setup/<step>` URLs, twins of the migrated steps.
 */
export function JourneysWizardStepHandoff({ step }: JourneysWizardStepHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.CsvImportsMigration);
  const importId = useImportId();
  const migratedHref = useMigrationHandoffHref((workspaceId) => importStepHref(Number(workspaceId), importId, step));

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}
